import React, { useState, useEffect, useRef } from 'react';
import { TestState, SpeedTestResult, ConnectionInfo, Theme, CompactSharedTestResult, LinkQuality, TestMode, StreamingAnalysis } from '../types';
import { Server } from 'shared-types';
import { DownloadIcon, PingIcon, UploadIcon, JitterIcon, ShareIcon, InfoIcon, SparklesIcon, ShieldCheckIcon, ShieldExclamationIcon, MessageSquareIcon, PlayIcon, CheckIcon, ChevronDownIcon, MapPinIcon } from 'shared-icons';
import { formatSpeed, calculateLinkQuality } from 'shared-grading';
import ServerSelector from './ServerSelector';

interface SpeedTestUIProps {
  testState: TestState;
  startTest: () => void;
  currentSpeed: number;
  results: SpeedTestResult;
  connectionInfo: ConnectionInfo | null;
  pingHistory: number[];
  jitterHistory: number[];
  servers: Server[];
  selectedServer: Server;
  setSelectedServer: (server: Server) => void;
  isFindingBestServer: boolean;
  manualServerSelection: boolean;
  setManualServerSelection: (manual: boolean) => void;
  onOpenCustomServerModal: () => void;
  isOnline: boolean;
  connectionMode: 'single' | 'multi';
  setConnectionMode: (mode: 'single' | 'multi') => void;
  theme: Theme;
  onAnalyze: () => void;
  isAnalyzing: boolean;
  testError: string | null;
  onOpenFeedbackModal: () => void;
  linkQuality: LinkQuality | null;
  testMode: TestMode;
  setTestMode: (mode: TestMode) => void;
  streamingAnalysis: StreamingAnalysis[] | null;
  isAnalyzingStreaming: boolean;
  streamingError: string | null;
  lastCompletedMode: TestMode | null;
}

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

const useCountUp = (end: number, duration: number = 1000) => {
  const [count, setCount] = useState(0);
  const frameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    setCount(0);
    startTimeRef.current = null;
    
    const animate = (timestamp: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = timestamp;
      }
      
      const elapsedTime = timestamp - startTimeRef.current;
      const progress = Math.min(elapsedTime / duration, 1);
      
      const easedProgress = easeOutCubic(progress);
      const currentVal = easedProgress * end;
      
      setCount(currentVal);
      
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    
    if (end > 0) {
      frameRef.current = requestAnimationFrame(animate);
    } else {
      setCount(0);
    }
    
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [end, duration]);

  return count;
};

const AnimatedNumber: React.FC<{ value: number; duration?: number; decimals?: number }> = ({ value, duration = 1500, decimals = 2 }) => {
  const animatedValue = useCountUp(value, duration);
  return <>{animatedValue.toFixed(decimals)}</>;
};

const ResultsGauge: React.FC<{
  download: number;
  upload: number;
  onHoverChange: (metric: 'download' | 'upload' | null) => void;
}> = ({ download, upload, onHoverChange }) => {
  const radius = 92;
  const strokeWidth = 15;
  const innerRadius = radius - strokeWidth;
  const circumference = 2 * Math.PI * radius;
  const innerCircumference = 2 * Math.PI * innerRadius;

  const speedToPercentage = (speed: number) => {
    // Logarithmic scale, where 1000 Mbps is ~100%
    const maxSpeed = 1000;
    const percentage = (Math.log(speed + 1) / Math.log(maxSpeed + 1)) * 100;
    return Math.min(percentage, 100); // Cap at 100%
  };

  const [downloadPercent, setDownloadPercent] = useState(0);
  const [uploadPercent, setUploadPercent] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDownloadPercent(speedToPercentage(download));
      setUploadPercent(speedToPercentage(upload));
    }, 100); 
    return () => clearTimeout(timer);
  }, [download, upload]);
  
  const downloadOffset = circumference - (downloadPercent / 100) * circumference;
  const uploadOffset = innerCircumference - (uploadPercent / 100) * innerCircumference;

  return (
    <div className="relative w-56 h-56 sm:w-64 sm:h-64">
      <svg className="w-full h-full" viewBox="0 0 200 200">
        <defs>
          <linearGradient id="downloadGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#06b6d4" />
            <stop offset="100%" stopColor="#3b82f6" />
          </linearGradient>
          <linearGradient id="uploadGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#a855f7" />
            <stop offset="100%" stopColor="#ec4899" />
          </linearGradient>
        </defs>
        <g transform="rotate(-90 100 100)">
          {/* Background circles */}
          <circle cx="100" cy="100" r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-gray-200 dark:text-slate-700" opacity="0.5" />
          <circle cx="100" cy="100" r={innerRadius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-gray-200 dark:text-slate-700" opacity="0.5" />

          {/* Download Arc Group */}
          <g onMouseEnter={() => onHoverChange('download')} onMouseLeave={() => onHoverChange(null)} className="cursor-pointer">
            <circle cx="100" cy="100" r={radius} fill="none" stroke="transparent" strokeWidth={strokeWidth + 4} />
            <circle
              cx="100"
              cy="100"
              r={radius}
              fill="none"
              stroke="url(#downloadGradient)"
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={downloadOffset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 1.5s cubic-bezier(0.25, 1, 0.5, 1)' }}
            />
          </g>
          
          {/* Upload Arc Group */}
          <g onMouseEnter={() => onHoverChange('upload')} onMouseLeave={() => onHoverChange(null)} className="cursor-pointer">
             <circle cx="100" cy="100" r={innerRadius} fill="none" stroke="transparent" strokeWidth={strokeWidth + 4} />
            <circle
              cx="100"
              cy="100"
              r={innerRadius}
              fill="none"
              stroke="url(#uploadGradient)"
              strokeWidth={strokeWidth}
              strokeDasharray={innerCircumference}
              strokeDashoffset={uploadOffset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 1.5s cubic-bezier(0.25, 1, 0.5, 1)' }}
            />
          </g>
        </g>
      </svg>
    </div>
  );
};

const RealtimeChart: React.FC<{ data: number[], strokeColor: string, height?: number, width?: number }> = ({ data, strokeColor, height = 40, width = 200 }) => {
  if (data.length < 2) {
    return <div style={{height, width}} className="bg-gray-100 dark:bg-slate-800/30 rounded-md" />;
  }
  
  const dataMax = data.length > 0 ? Math.max(...data) : 0;
  const maxVal = Math.min(200, Math.max(50, dataMax)); 
  
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - (Math.min(d, maxVal) / maxVal) * height;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ');

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="bg-gray-100 dark:bg-slate-800/30 rounded-md">
      <polyline
        fill="none"
        stroke={strokeColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
};


const LinkQualityDisplay: React.FC<{ quality: LinkQuality }> = ({ quality }) => {
    const Icon = quality.grade === 'A' || quality.grade === 'B' ? ShieldCheckIcon : ShieldExclamationIcon;
    const [barWidth, setBarWidth] = useState(0);

    useEffect(() => {
        // Trigger the animation after the component mounts
        const timer = setTimeout(() => setBarWidth(quality.score), 100);
        return () => clearTimeout(timer);
    }, [quality.score]);

    if (quality.isError) {
        return (
             <div className="w-full text-center p-4 bg-white/20 dark:bg-slate-800/20 backdrop-blur-sm border border-white/30 dark:border-slate-700/50 rounded-lg">
                <div className="flex items-center justify-center gap-3">
                    <InfoIcon width={28} height={28} className={quality.color} />
                    <div>
                        <h3 className={`text-xl font-bold ${quality.color}`}>{quality.title}</h3>
                        <p className="text-sm text-gray-600 dark:text-slate-400">{quality.description}</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="w-full text-center p-4 bg-white/20 dark:bg-slate-800/20 backdrop-blur-sm border border-white/30 dark:border-slate-700/50 rounded-lg">
            <div className="flex items-center justify-center gap-3">
                <Icon width={32} height={32} className={quality.color} />
                <div>
                    <h3 className={`text-xl font-bold ${quality.color}`}>{quality.title} (Grade {quality.grade})</h3>
                     <p className="text-sm text-gray-600 dark:text-slate-400">{quality.description}</p>
                </div>
            </div>
            <div className="mt-3">
                <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
                    <div
                        className={`h-2.5 rounded-full ${quality.color.replace('text-', 'bg-')}`}
                        style={{ width: `${barWidth}%`, transition: 'width 1.5s cubic-bezier(0.25, 1, 0.5, 1)' }}
                    ></div>
                </div>
            </div>
            {quality.historyContext && (
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-2 italic">{quality.historyContext}</p>
            )}
        </div>
    );
};

const ResultDisplay: React.FC<{
  results: SpeedTestResult;
  onCopyLink: () => void;
  shareStatus: 'idle' | 'copied';
  onAnalyze: () => void;
  isAnalyzing: boolean;
  startTest: () => void;
  isOnline: boolean;
  onOpenFeedbackModal: () => void;
  linkQuality: LinkQuality;
}> = ({ results, onCopyLink, shareStatus, onAnalyze, isAnalyzing, startTest, isOnline, onOpenFeedbackModal, linkQuality }) => {
    const [hoveredMetric, setHoveredMetric] = useState<'download' | 'upload' | null>(null);
    const formattedDownload = formatSpeed(results.download);
    const formattedUpload = formatSpeed(results.upload);
    
    return (
    <div className="w-full max-w-lg text-center flex flex-col items-center gap-4 result-final">
        <div className="relative w-56 h-56 sm:w-64 sm:h-64 flex items-center justify-center">
            <ResultsGauge
              download={results.download}
              upload={results.upload}
              onHoverChange={setHoveredMetric}
            />
            <button
                onClick={startTest}
                disabled={!isOnline}
                className="absolute w-40 h-40 sm:w-48 sm:h-48 bg-white/30 dark:bg-slate-800/40 backdrop-blur-md border border-white/50 dark:border-slate-700/60 rounded-full text-slate-900 dark:text-white text-5xl sm:text-6xl font-bold shadow-2xl flex items-center justify-center
                            transform transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-cyan-500/50
                            disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
            >
                GO
            </button>
        </div>

        {/* Hover Display Area */}
        <div className="w-full h-28 flex-col items-center justify-center p-2 text-center hidden sm:flex">
            {hoveredMetric ? (
                <div className={`transition-opacity duration-300 ${hoveredMetric ? 'opacity-100' : 'opacity-0'}`}>
                {hoveredMetric === 'download' && (
                    <div className="text-cyan-600 dark:text-cyan-400">
                    <DownloadIcon className="mx-auto" />
                    <div className="font-bold text-lg mt-1">Download</div>
                    <div className="text-3xl font-bold text-slate-800 dark:text-white">
                        <AnimatedNumber value={parseFloat(formattedDownload.value)} decimals={formattedDownload.value.includes('.') ? formattedDownload.value.split('.')[1].length : 0} />
                    </div>
                    <div className="text-gray-600 dark:text-slate-500 -mt-1">{formattedDownload.unit}</div>
                    </div>
                )}
                {hoveredMetric === 'upload' && (
                    <div className="text-purple-600 dark:text-purple-400">
                    <UploadIcon className="mx-auto" />
                    <div className="font-bold text-lg mt-1">Upload</div>
                     <div className="text-3xl font-bold text-slate-800 dark:text-white">
                        <AnimatedNumber value={parseFloat(formattedUpload.value)} decimals={formattedUpload.value.includes('.') ? formattedUpload.value.split('.')[1].length : 0} />
                    </div>
                    <div className="text-gray-600 dark:text-slate-500 -mt-1">{formattedUpload.unit}</div>
                    </div>
                )}
                </div>
            ) : (
                <div className="text-gray-400 dark:text-slate-500 text-sm transition-opacity duration-300">
                Hover over the gauge arcs for details
                </div>
            )}
        </div>

        <LinkQualityDisplay quality={linkQuality} />

        <div className="w-full grid grid-cols-2 gap-4">
            <div className="flex flex-col items-center justify-center p-4 bg-white/20 dark:bg-slate-800/20 backdrop-blur-sm border border-white/30 dark:border-slate-700/50 rounded-lg">
                <div className="flex items-center justify-center gap-1.5 text-cyan-500 dark:text-cyan-400">
                    <DownloadIcon />
                    <span>Download</span>
                    <div className="relative group">
                      <InfoIcon width={16} height={16} className="text-slate-400 dark:text-slate-500" />
                      <div className="absolute bottom-full mb-2 w-64 max-w-[80vw] p-2 bg-slate-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-left left-0">
                          <strong>Download Speed:</strong> The speed at which you can pull data from a server to your device. Higher is better for streaming, downloading files, and browsing.
                      </div>
                    </div>
                    {results.downloadError && (
                      <div className="relative group">
                        <ShieldExclamationIcon width={16} height={16} className="text-yellow-500" />
                        <div className="absolute bottom-full mb-2 w-64 max-w-[80vw] p-2 bg-slate-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-left left-0">
                            <strong>Test Note:</strong> {results.downloadError}
                        </div>
                      </div>
                    )}
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-slate-800 dark:text-white">
                    <AnimatedNumber value={parseFloat(formattedDownload.value)} decimals={formattedDownload.value.includes('.') ? formattedDownload.value.split('.')[1].length : 0} />
                </div>
                <div className="text-gray-600 dark:text-slate-500">{formattedDownload.unit}</div>
            </div>
            <div className="flex flex-col items-center justify-center p-4 bg-white/20 dark:bg-slate-800/20 backdrop-blur-sm border border-white/30 dark:border-slate-700/50 rounded-lg">
                <div className="flex items-center justify-center gap-1.5 text-purple-500 dark:text-purple-400">
                    <UploadIcon />
                    <span>Upload</span>
                    <div className="relative group">
                      <InfoIcon width={16} height={16} className="text-slate-400 dark:text-slate-500" />
                       <div className="absolute bottom-full mb-2 w-64 max-w-[80vw] p-2 bg-slate-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-left right-0">
                          <strong>Upload Speed:</strong> The speed at which you can send data from your device to a server. Higher is better for video calls, uploading files, and online gaming.
                      </div>
                    </div>
                     {results.uploadError && (
                      <div className="relative group">
                        <ShieldExclamationIcon width={16} height={16} className="text-yellow-500" />
                        <div className="absolute bottom-full mb-2 w-64 max-w-[80vw] p-2 bg-slate-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-left right-0">
                            <strong>Test Note:</strong> {results.uploadError}
                        </div>
                      </div>
                    )}
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-slate-800 dark:text-white">
                    <AnimatedNumber value={parseFloat(formattedUpload.value)} decimals={formattedUpload.value.includes('.') ? formattedUpload.value.split('.')[1].length : 0} />
                </div>
                <div className="text-gray-600 dark:text-slate-500">{formattedUpload.unit}</div>
            </div>
             <div className="flex flex-col items-center justify-center p-4 bg-white/20 dark:bg-slate-800/20 backdrop-blur-sm border border-white/30 dark:border-slate-700/50 rounded-lg">
                <div className="flex items-center justify-center gap-1.5 text-gray-600 dark:text-slate-400">
                    <PingIcon />
                    <span>Ping</span>
                    <div className="relative group">
                      <InfoIcon width={16} height={16} className="text-slate-400 dark:text-slate-500" />
                      <div className="absolute bottom-full mb-2 w-64 max-w-[80vw] p-2 bg-slate-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-left left-0">
                          <strong>Ping (Latency):</strong> The reaction time of your connection. Lower is better for responsive online gaming and real-time communication.
                      </div>
                    </div>
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-slate-800 dark:text-white"><AnimatedNumber value={results.ping} decimals={0} /></div>
                <div className="text-gray-600 dark:text-slate-500">ms</div>
            </div>
            <div className="flex flex-col items-center justify-center p-4 bg-white/20 dark:bg-slate-800/20 backdrop-blur-sm border border-white/30 dark:border-slate-700/50 rounded-lg">
                <div className="flex items-center justify-center gap-1.5 text-gray-600 dark:text-slate-400">
                    <JitterIcon />
                    <span>Jitter</span>
                    <div className="relative group">
                      <InfoIcon width={16} height={16} className="text-slate-400 dark:text-slate-500" />
                      <div className="absolute bottom-full mb-2 w-64 max-w-[80vw] p-2 bg-slate-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-left right-0">
                         <strong>Jitter:</strong> The variation in your ping over time. Lower is better for smooth, consistent voice and video calls.
                      </div>
                    </div>
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-slate-800 dark:text-white"><AnimatedNumber value={results.jitter} decimals={0} /></div>
                <div className="text-gray-600 dark:text-slate-500">ms</div>
            </div>
        </div>

        <div className="w-full flex flex-col items-stretch justify-center gap-3">
             <button
                onClick={onCopyLink}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white/30 dark:bg-slate-800/30 backdrop-blur-sm border border-white/40 dark:border-slate-700/50 text-slate-800 dark:text-slate-200 font-semibold rounded-full shadow-lg shadow-slate-500/10 hover:shadow-slate-500/20 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-opacity-75 transition-all"
            >
                <ShareIcon />
                <span>{shareStatus === 'copied' ? 'Copied!' : 'Share Result'}</span>
            </button>
            <div className="flex items-stretch justify-center gap-3">
                <button
                    onClick={onAnalyze}
                    disabled={isAnalyzing}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white/40 dark:bg-slate-700/40 backdrop-blur-sm border border-white/50 dark:border-slate-600/50 text-slate-900 dark:text-white font-semibold rounded-full shadow-lg shadow-slate-500/10 hover:shadow-slate-500/20 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-75 transition-all disabled:opacity-50 disabled:cursor-wait"
                >
                    <SparklesIcon />
                    <span>{isAnalyzing ? 'Analyzing...' : 'Analyze AI'}</span>
                </button>
                <button
                    onClick={onOpenFeedbackModal}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white/30 dark:bg-slate-800/30 backdrop-blur-sm border border-white/40 dark:border-slate-700/50 text-slate-800 dark:text-slate-200 font-semibold rounded-full shadow-lg shadow-slate-500/10 hover:shadow-slate-500/20 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-opacity-75 transition-all"
                >
                    <MessageSquareIcon />
                    <span>Feedback</span>
                </button>
            </div>
        </div>
    </div>
)};

const ConnectionModeSelector: React.FC<{
  mode: 'single' | 'multi';
  setMode: (mode: 'single' | 'multi') => void;
  isDisabled: boolean;
}> = ({ mode, setMode, isDisabled }) => {
  return (
    <div>
        <div className="flex justify-between items-center mb-2">
           <div className="flex items-center gap-2 group relative">
             <label className="text-lg font-semibold text-slate-900 dark:text-slate-100">Connection Type</label>
             <div className="text-slate-500 dark:text-slate-400"><InfoIcon /></div>
             <div className="absolute bottom-full left-0 sm:left-1/2 sm:-translate-x-1/2 mb-2 w-72 max-w-[calc(100vw-4rem)] sm:max-w-xs p-2 bg-slate-800 dark:bg-slate-700 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                <strong>Multi:</strong> Uses several connections to find your max speed. Best for general testing.
                <br /><br />
                <strong>Single:</strong> Uses one connection, like a single file download. Useful for specific diagnostics.
             </div>
           </div>
        </div>
        <div className={`flex rounded-full p-1 bg-white/20 dark:bg-slate-800/20 backdrop-blur-sm ring-1 ring-black/5 dark:ring-white/10 transition-opacity ${isDisabled ? 'opacity-50' : ''}`}>
            <button
                onClick={() => setMode('single')}
                disabled={isDisabled}
                className={`flex-1 py-2 px-4 text-center rounded-full font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${mode === 'single' ? 'bg-white/40 dark:bg-slate-700/50 shadow-sm text-slate-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300 hover:bg-white/20 dark:hover:bg-slate-700/20'}`}
                aria-pressed={mode === 'single'}
            >
                Single
            </button>
             <button
                onClick={() => setMode('multi')}
                disabled={isDisabled}
                className={`flex-1 py-2 px-4 text-center rounded-full font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${mode === 'multi' ? 'bg-white/40 dark:bg-slate-700/50 shadow-sm text-slate-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300 hover:bg-white/20 dark:hover:bg-slate-700/20'}`}
                aria-pressed={mode === 'multi'}
            >
                Multi
            </button>
        </div>
    </div>
  );
};

const TestModeSelector: React.FC<{
  mode: TestMode;
  setMode: (mode: TestMode) => void;
  isDisabled: boolean;
}> = ({ mode, setMode, isDisabled }) => {
  return (
    <div className={`flex rounded-full p-1 bg-white/20 dark:bg-slate-800/20 backdrop-blur-sm ring-1 ring-black/5 dark:ring-white/10 transition-opacity ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <button
            onClick={() => setMode('speed')}
            disabled={isDisabled}
            className={`flex-1 py-2 px-6 text-center rounded-full font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${mode === 'speed' ? 'bg-white/40 dark:bg-slate-700/50 shadow-sm text-slate-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-400 hover:bg-white/20 dark:hover:bg-slate-700/20'}`}
            aria-pressed={mode === 'speed'}
        >
            Speed
        </button>
        <button
            onClick={() => setMode('streaming')}
            disabled={isDisabled}
            className={`flex-1 py-2 px-6 text-center rounded-full font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${mode === 'streaming' ? 'bg-white/40 dark:bg-slate-700/50 shadow-sm text-slate-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-400 hover:bg-white/20 dark:hover:bg-slate-700/20'}`}
            aria-pressed={mode === 'streaming'}
        >
            Streaming
        </button>
    </div>
  );
};

const StreamingAnalysisResults: React.FC<{
    results: SpeedTestResult;
    analysis: StreamingAnalysis[] | null;
    isLoading: boolean;
    error: string | null;
    onRestart: () => void;
}> = ({ results, analysis, isLoading, error, onRestart }) => {
    
    const linkQuality = calculateLinkQuality(results);

    const ratingMap: { [key: string]: { color: string; Icon: React.FC<any> } } = {
        Excellent: { color: 'text-green-500 dark:text-green-400', Icon: ShieldCheckIcon },
        Good: { color: 'text-cyan-500 dark:text-cyan-400', Icon: ShieldCheckIcon },
        Fair: { color: 'text-yellow-500 dark:text-yellow-400', Icon: ShieldExclamationIcon },
        Poor: { color: 'text-red-500 dark:text-red-400', Icon: ShieldExclamationIcon },
    };
    
    if (isLoading) {
        return (
            <div className="w-full max-w-lg text-center flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
                <p className="text-lg text-slate-600 dark:text-slate-400">AI is analyzing your streaming quality...</p>
            </div>
        );
    }
    
    if (error) {
         return (
            <div className="w-full max-w-lg text-center flex flex-col items-center gap-4">
                <ShieldExclamationIcon className="w-16 h-16 text-red-500/80" />
                <h3 className="text-xl font-bold">Analysis Failed</h3>
                <p className="text-slate-600 dark:text-slate-400 bg-red-100 dark:bg-red-900/20 p-3 rounded-md">{error}</p>
                <button onClick={onRestart} className="mt-4 px-6 py-3 bg-white/30 dark:bg-slate-800/40 backdrop-blur-md border border-white/50 dark:border-slate-700/60 text-slate-900 dark:text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all">Test Again</button>
            </div>
        );
    }
    
    if (!analysis) return null;

    return (
        <div className="w-full max-w-lg text-center flex flex-col items-center gap-6">
            <LinkQualityDisplay quality={linkQuality} />
            
            <div className="w-full flex flex-col items-center gap-4">
                <div className="flex items-center gap-3">
                    <SparklesIcon className="w-8 h-8 text-purple-500" />
                    <h2 className="text-2xl font-bold">Streaming Quality Analysis</h2>
                </div>
                <div className="w-full space-y-3">
                    {analysis.map(item => {
                        const ratingInfo = ratingMap[item.quality] || ratingMap.Poor;
                        return (
                            <div key={item.resolution} className="bg-white/20 dark:bg-slate-800/20 backdrop-blur-sm border border-white/30 dark:border-slate-700/50 rounded-lg p-4 text-left">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">{item.resolution}</h3>
                                    <div className={`flex items-center gap-1.5 text-sm font-semibold ${ratingInfo.color}`}>
                                        <ratingInfo.Icon className="w-4 h-4" />
                                        <span>{item.quality}</span>
                                    </div>
                                </div>
                                <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{item.description}</p>
                            </div>
                        )
                    })}
                </div>
            </div>
             <button onClick={onRestart} className="mt-4 px-8 py-3 bg-white/30 dark:bg-slate-800/40 backdrop-blur-md border border-white/50 dark:border-slate-700/60 text-slate-900 dark:text-white text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all">Test Again</button>
        </div>
    );
};

const SpeedTestUI: React.FC<SpeedTestUIProps> = (props) => {
  const { 
    testState, startTest, currentSpeed, results, connectionInfo, pingHistory, jitterHistory, 
    servers, selectedServer, setSelectedServer, isFindingBestServer, manualServerSelection, setManualServerSelection,
    onOpenCustomServerModal, isOnline, connectionMode, setConnectionMode, theme, onAnalyze, 
    isAnalyzing, testError, onOpenFeedbackModal, linkQuality,
    testMode, setTestMode, streamingAnalysis, isAnalyzingStreaming, streamingError, lastCompletedMode
  } = props;
  
  const isTesting = testState !== 'idle' && testState !== 'finished' && testState !== 'error';
  const [shareStatus, setShareStatus] = useState<'idle' | 'copied'>('idle');
  const [maxBarSpeed, setMaxBarSpeed] = useState(1000);
  const [shareableUrl, setShareableUrl] = useState<string | null>(null);

  const totalTicks = 40;
  const maxTickSpeed = 200;

  useEffect(() => {
    if (testState === 'finished' && connectionInfo) {
      const payload: CompactSharedTestResult = {
        v: 2,
        dt: new Date().toISOString(),
        r: {
          d: results.download,
          u: results.upload,
          p: results.ping,
          j: results.jitter,
        },
        c: {
          i: connectionInfo.isp,
          l: connectionInfo.serverLocation,
        },
        s: {
          n: selectedServer.name,
          l: selectedServer.location,
        },
      };

      const jsonString = JSON.stringify(payload);
      const base64String = btoa(jsonString);
      const baseUrl = `${window.location.origin}${window.location.pathname}`;
      const url = `${baseUrl}?result=${encodeURIComponent(base64String)}`;
      setShareableUrl(url);
    } else {
      // Reset the URL when a new test starts or state changes
      setShareableUrl(null);
    }
  }, [testState, results, connectionInfo, selectedServer]);


  useEffect(() => {
    if (testState === 'testing-ping') {
      setMaxBarSpeed(1000); // Reset on new test
      return;
    }

    if (isTesting) {
      let newMax = maxBarSpeed;
      if (currentSpeed > 4500 && maxBarSpeed < 5000) newMax = 5000;
      else if (currentSpeed > 1800 && maxBarSpeed < 2000) newMax = 2000;
      else if (currentSpeed > 900 && maxBarSpeed < 1001) newMax = 2000;
      
      if (newMax !== maxBarSpeed) setMaxBarSpeed(newMax);
    }
  }, [currentSpeed, testState, isTesting, maxBarSpeed]);
  
  const getStatusText = () => {
    switch (testState) {
      case 'testing-ping': return 'Testing Ping & Jitter...';
      case 'testing-download': return 'Download';
      case 'testing-upload': return 'Upload';
      case 'finished': return 'Test Complete';
      case 'error': return 'Test Failed';
      default: return 'Ready to Test';
    }
  };

  const handleCopyLink = async () => {
    if (!shareableUrl || shareStatus === 'copied' || !linkQuality) return;

    const summary = `My Teez Speed Test: ${results.download.toFixed(1)} Mbps Download / ${results.upload.toFixed(1)} Mbps Upload – Grade ${linkQuality.grade} (${linkQuality.title})\n\nTest your speed: ${shareableUrl}`;

    try {
      await navigator.clipboard.writeText(summary);
      setShareStatus('copied');
      setTimeout(() => setShareStatus('idle'), 2500);
    } catch (err) {
      console.error('Failed to copy result summary:', err);
      alert('Failed to copy result summary to clipboard.');
    }
  };
  
  const activeTicks = Math.round((Math.min(currentSpeed, maxTickSpeed) / maxTickSpeed) * totalTicks);

  const getBarColor = (index: number, total: number) => {
    const progress = index / total;
    const cyan = { r: 34, g: 211, b: 238 };
    const purple = { r: 168, g: 85, b: 247 };
    const magenta = { r: 236, g: 72, b: 153 };
    let r, g, b;
    if (progress < 0.5) {
      const p = progress * 2;
      r = Math.round(cyan.r + (purple.r - cyan.r) * p);
      g = Math.round(cyan.g + (purple.g - cyan.g) * p);
      b = Math.round(cyan.b + (purple.b - cyan.b) * p);
    } else {
      const p = (progress - 0.5) * 2;
      r = Math.round(purple.r + (magenta.r - purple.r) * p);
      g = Math.round(purple.g + (magenta.g - purple.g) * p);
      b = Math.round(purple.b + (magenta.b - purple.b) * p);
    }
    return `rgb(${r}, ${g}, ${b})`;
  };

  const getSpeedColor = (speed: number, maxSpeed: number) => {
    if (maxSpeed <= 0) return 'rgb(34, 211, 238)'; // Return default color if maxSpeed is 0
    const progress = Math.min(speed, maxSpeed) / maxSpeed;
    const cyan = { r: 34, g: 211, b: 238 };
    const purple = { r: 168, g: 85, b: 247 };
    const magenta = { r: 236, g: 72, b: 153 };
    let r, g, b;
    if (progress < 0.5) {
      const p = progress * 2;
      r = Math.round(cyan.r + (purple.r - cyan.r) * p);
      g = Math.round(cyan.g + (purple.g - cyan.g) * p);
      b = Math.round(cyan.b + (purple.b - cyan.b) * p);
    } else {
      const p = (progress - 0.5) * 2;
      r = Math.round(purple.r + (magenta.r - purple.r) * p);
      g = Math.round(purple.g + (magenta.g - purple.g) * p);
      b = Math.round(purple.b + (magenta.b - purple.b) * p);
    }
    return `rgb(${r}, ${g}, ${b})`;
  };

  const barProgress = maxBarSpeed > 0 ? Math.min(100, (currentSpeed / maxBarSpeed) * 100) : 0;
  const isBarVisible = testState === 'testing-download' || testState === 'testing-upload';
  const tickColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  
  const shouldShowResults = testState === 'finished' && testMode === lastCompletedMode;
  const currentSpeedFormatted = formatSpeed(currentSpeed);
  const speedColor = getSpeedColor(currentSpeed, maxBarSpeed);

  return (
    <div className="flex flex-col items-center gap-8 py-4 w-full">
      <TestModeSelector mode={testMode} setMode={setTestMode} isDisabled={isTesting} />
      
      {/* Main Dynamic Stage */}
      <div className="w-full flex flex-col items-center justify-center min-h-[400px]">
        {shouldShowResults && linkQuality ? (
          <div className="flex flex-col items-center gap-6 w-full">
            <div className="text-xl text-gray-700 dark:text-slate-300 font-semibold h-7">Test Complete</div>
            {testMode === 'speed' ? (
                <ResultDisplay 
                  results={results} 
                  onCopyLink={handleCopyLink} 
                  shareStatus={shareStatus} 
                  onAnalyze={onAnalyze} 
                  isAnalyzing={isAnalyzing}
                  startTest={startTest}
                  isOnline={isOnline}
                  onOpenFeedbackModal={onOpenFeedbackModal}
                  linkQuality={linkQuality}
                />
            ) : (
                <StreamingAnalysisResults
                    results={results}
                    analysis={streamingAnalysis}
                    isLoading={isAnalyzingStreaming}
                    error={streamingError}
                    onRestart={startTest}
                />
            )}
          </div>
        ) : testState === 'error' && linkQuality ? (
          <div className="flex flex-col items-center gap-4 text-center p-4">
            <div className="text-xl text-gray-700 dark:text-slate-300 font-semibold h-7">Test Failed</div>
             <ShieldExclamationIcon className="w-24 h-24 text-red-500/80" />
            <LinkQualityDisplay quality={linkQuality} />
            <button
              onClick={startTest}
              className="mt-4 w-40 h-40 sm:w-48 sm:h-48 bg-white/30 dark:bg-slate-800/40 backdrop-blur-md border border-white/50 dark:border-slate-700/60 rounded-full text-slate-900 dark:text-white text-4xl sm:text-5xl font-bold shadow-2xl flex items-center justify-center
                          transform transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-purple-500/50"
            >
              AGAIN
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 w-full">
            <div className="text-xl text-gray-700 dark:text-slate-300 font-semibold h-7">{getStatusText()}</div>
            <div className="flex flex-col items-center w-full max-w-xs">
              <div className="relative w-56 h-56 sm:w-64 sm:h-64 flex items-center justify-center">
                <svg className="absolute w-full h-full" viewBox="0 0 200 200">
                  <g transform="translate(100, 100)">
                    {Array.from({ length: totalTicks }).map((_, index) => (
                      <rect
                        key={index}
                        className={index === activeTicks - 1 && isTesting ? 'gauge-needle-head' : ''}
                        x="-2.5"
                        y={index === activeTicks - 1 && isTesting ? -94 : -92}
                        width="5"
                        height={index === activeTicks - 1 && isTesting ? 18 : 14}
                        rx="2.5"
                        ry="2.5"
                        fill={index < activeTicks ? getBarColor(index, totalTicks) : tickColor}
                        transform={`rotate(${(index * 360) / totalTicks - 90})`}
                        style={{ transition: 'fill 200ms ease-out, height 200ms ease-out, y 200ms ease-out' }}
                      />
                    ))}
                  </g>
                </svg>
                { isTesting ? (
                    <div className="text-center z-10">
                        <div className="font-bold text-5xl sm:text-6xl tracking-tighter" style={{ color: speedColor, transition: 'color 200ms ease-out' }}>
                            {currentSpeedFormatted.value}
                        </div>
                        <div className="text-gray-600 dark:text-slate-400 text-lg -mt-1">{currentSpeedFormatted.unit}</div>
                    </div>
                ) : (
                    <button
                        onClick={startTest}
                        disabled={!isOnline}
                        className="z-10 w-40 h-40 sm:w-48 sm:h-48 bg-white/30 dark:bg-slate-800/40 backdrop-blur-md border border-white/50 dark:border-slate-700/60 rounded-full text-slate-900 dark:text-white text-5xl sm:text-6xl font-bold shadow-2xl flex items-center justify-center
                                    transform transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-cyan-500/50
                                    disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
                    >
                        <span className="z-10">GO</span>
                    </button>
                )}
              </div>
              <div className={`w-full mt-4 transition-opacity duration-300 ${isBarVisible ? 'opacity-100' : 'opacity-0'}`}>
                  <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                      <div className="bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 h-2 rounded-full transition-all duration-200 ease-linear" style={{ width: `${barProgress}%` }} />
                  </div>
                  <div className="flex justify-between text-xs text-gray-600 dark:text-slate-400 mt-1">
                      <span>0</span>
                      <span>{maxBarSpeed} Mbps</span>
                  </div>
              </div>
            </div>
            {testState === 'testing-ping' && (
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-center mt-4 transition-opacity duration-300">
                  <div className="flex items-center gap-2"><span className="text-gray-600 dark:text-slate-400 font-medium">Ping</span><RealtimeChart data={pingHistory} strokeColor="#06b6d4" /></div>
                  <div className="flex items-center gap-2"><span className="text-gray-600 dark:text-slate-400 font-medium">Jitter</span><RealtimeChart data={jitterHistory} strokeColor="#a855f7" /></div>
              </div>
            )}
          </div>
        )}
      </div>

       {/* Connection Mode Display */}
      {(testState === 'testing-download' || testState === 'testing-upload') && (
        <div className="text-gray-500 dark:text-slate-400 text-lg capitalize font-bold tracking-wider transition-opacity duration-300 h-8 flex items-center justify-center -mt-8">
            {connectionMode}
        </div>
      )}

      {/* Static Info & Controls */}
      <div className="text-center h-12 flex flex-col justify-center">
        {connectionInfo ? (
          <>
            <p className={`text-xl font-semibold text-slate-900 dark:text-slate-100 ${connectionInfo.error ? 'text-yellow-600 dark:text-yellow-400' : ''}`}>
               {connectionInfo.as && !connectionInfo.error && <span className="text-gray-600 dark:text-slate-400">{connectionInfo.as}</span>} {connectionInfo.isp}
            </p>
            <p className="text-sm text-gray-600 dark:text-slate-400">{connectionInfo.ip} — {connectionInfo.serverLocation}</p>
          </>
        ) : (
          <p>Getting connection info...</p>
        )}
      </div>

      <div className="w-full max-w-sm mx-auto space-y-8">
        <ServerSelector
          servers={servers}
          selectedServer={selectedServer}
          onSelectServer={setSelectedServer}
          isLoading={isFindingBestServer}
          isManual={manualServerSelection}
          onSetManual={setManualServerSelection}
          onAddCustom={onOpenCustomServerModal}
          isDisabled={isTesting}
        />
        
        <ConnectionModeSelector
          mode={connectionMode}
          setMode={setConnectionMode}
          isDisabled={isTesting}
        />
      </div>

    </div>
  );
};

export default SpeedTestUI;